import React from 'react';
import { LucideIcon } from 'lucide-react';

interface SquareButtonProps {
  /** Icono a mostrar en el botón */
  icon: LucideIcon;
  /** Texto del botón */
  text: string;
  /** Función a ejecutar al hacer click */
  onClick: () => void;
  /** Tamaño del botón */
  size?: 'small' | 'medium' | 'large';
  /** Variante de color del botón */
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  /** Si el botón está deshabilitado */
  disabled?: boolean;
  /** Clases CSS adicionales */
  className?: string;
}

/**
 * Componente de botón cuadrado reutilizable con diferentes tamaños y colores
 */
export const SquareButton: React.FC<SquareButtonProps> = ({
  icon: Icon,
  text,
  onClick,
  size = 'medium',
  variant = 'primary',
  disabled = false,
  className = ''
}) => {
  // Configuración de tamaños
  const sizeConfig = {
    small: {
      padding: 'p-4',
      icon: 'w-6 h-6',
      text: 'text-xs font-medium',
      spacing: 'space-y-2'
    },
    medium: {
      padding: 'p-6',
      icon: 'w-8 h-8',
      text: 'text-sm font-semibold',
      spacing: 'space-y-3'
    },
    large: {
      padding: 'p-8',
      icon: 'w-10 h-10',
      text: 'text-base font-bold',
      spacing: 'space-y-4'
    }
  };

  // Configuración de variantes de color (clases estáticas)
  const variantConfig = {
    primary: {
      background: 'bg-gradient-to-br from-primary-500/80 via-primary-600/70 to-primary-700/80',
      hover: 'hover:from-primary-400/90 hover:via-primary-500/80 hover:to-primary-600/90',
      active: 'active:from-primary-600/90 active:via-primary-700/80 active:to-primary-800/90',
      border: 'border-primary-400/30',
      hoverBorder: 'hover:border-primary-300/50'
    },
    secondary: {
      background: 'bg-gradient-to-br from-dark-card/80 via-dark-border/70 to-dark-light/80',
      hover: 'hover:from-dark-card/90 hover:via-dark-border/80 hover:to-dark-light/90',
      active: 'active:from-dark-border/90 active:via-dark-light/80 active:to-dark-light/90',
      border: 'border-dark-border/30',
      hoverBorder: 'hover:border-dark-light/50'
    },
    success: {
      background: 'bg-gradient-to-br from-green-500/80 via-green-600/70 to-green-700/80',
      hover: 'hover:from-green-400/90 hover:via-green-500/80 hover:to-green-600/90',
      active: 'active:from-green-600/90 active:via-green-700/80 active:to-green-800/90',
      border: 'border-green-400/30',
      hoverBorder: 'hover:border-green-300/50'
    },
    warning: {
      background: 'bg-gradient-to-br from-yellow-500/80 via-yellow-600/70 to-yellow-700/80',
      hover: 'hover:from-yellow-400/90 hover:via-yellow-500/80 hover:to-yellow-600/90',
      active: 'active:from-yellow-600/90 active:via-yellow-700/80 active:to-yellow-800/90',
      border: 'border-yellow-400/30',
      hoverBorder: 'hover:border-yellow-300/50'
    },
    error: {
      background: 'bg-gradient-to-br from-red-500/80 via-red-600/70 to-red-700/80',
      hover: 'hover:from-red-400/90 hover:via-red-500/80 hover:to-red-600/90',
      active: 'active:from-red-600/90 active:via-red-700/80 active:to-red-800/90',
      border: 'border-red-400/30',
      hoverBorder: 'hover:border-red-300/50'
    }
  };

  const config = sizeConfig[size];
  const colorClasses = variantConfig[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        aspect-square 
        ${colorClasses.background}
        ${colorClasses.hover}
        ${colorClasses.active}
        transition-all duration-300 
        rounded-2xl 
        ${config.padding} 
        flex flex-col items-center justify-center 
        ${config.spacing} 
        hover:scale-105 
        backdrop-blur-md 
        border ${colorClasses.border}
        ${colorClasses.hoverBorder}
        disabled:opacity-50 disabled:cursor-not-allowed
        disabled:hover:scale-100 disabled:active:scale-100
        ${className}
      `}
    >
      <Icon 
        className={`${config.icon} text-white drop-shadow-lg`}
      />
      <span className={`text-white ${config.text} text-center leading-tight drop-shadow-md`}>
        {text}
      </span>
    </button>
  );
};